import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile loader. Renders a managed-challenge widget that
// resolves to a token; we forward that token to /sms/v2 (sendSmsCode) where
// the lambda calls Cloudflare's siteverify before issuing the SMS code.
//
// Site key comes from VITE_TURNSTILE_SITE_KEY. When unset we render nothing
// and behave as a no-op (the lambda's verifier also fails open if the
// secret is missing, so dev keeps working).

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          'timeout-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact' | 'invisible';
          appearance?: 'always' | 'execute' | 'interaction-only';
          action?: string;
        },
      ) => string; // widget id
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

const loadTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC.split('?')[0]}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

interface TurnstileWidgetProps {
  onToken: (token: string | null) => void;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  // Cloudflare appearance modes:
  //   'interaction-only' (default) — widget is hidden unless a real challenge
  //                                  is needed. Most users never see it.
  //   'execute'                    — only renders the badge during execution.
  //   'always'                     — always visible (the original boxed UI).
  appearance?: 'always' | 'execute' | 'interaction-only';
}

export const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onToken,
  action,
  theme = 'auto',
  appearance = 'interaction-only',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    let cancelled = false;
    if (!siteKey || !containerRef.current) return;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          appearance,
          action,
          callback: (token) => onToken(token),
          'error-callback': () => onToken(null),
          'expired-callback': () => onToken(null),
          'timeout-callback': () => onToken(null),
        });
      })
      .catch(() => {
        // Silently ignore — backend still requires the token, so the user
        // will see the captcha-required error from the API instead.
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action, theme, appearance]);

  if (!siteKey) {
    // Dev safety: render a placeholder so it's obvious the widget is off.
    if (import.meta.env.DEV) {
      return (
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>
          (Turnstile disabled — set VITE_TURNSTILE_SITE_KEY)
        </div>
      );
    }
    return null;
  }

  // No reserved height: in interaction-only mode the widget collapses to
  // zero height when no challenge is required, so we don't leave a gap in
  // the layout. When a challenge IS needed the widget pushes the layout
  // down — that's expected.
  return <div ref={containerRef} />;
};
